import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, Loader2, RefreshCw, CheckCircle2, RotateCcw, Download, Search } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sep 2026 — Ledger Discrepancies review.
 *
 * Lists order lines the historical ledger correction set aside because
 * the shipped quantity was more than the pallet could account for.
 * Super admin checks each against physical stock and marks it reviewed.
 */
export default function AdminDiscrepancies() {
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('mr_admin_token') || ''}` }),
    [],
  );
  const [tab, setTab] = useState('open');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ open: 0, reviewed: 0 });
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/system/discrepancies`, {
        headers: authHeaders, params: { status: tab, q },
      });
      setRows(data.rows || []);
      setCounts(data.counts || { open: 0, reviewed: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load discrepancies');
    } finally {
      setBusy(false);
    }
  }, [authHeaders, tab, q]);

  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (row, action) => {
    setBusyId(row.id);
    try {
      await axios.post(`${API}/admin/system/discrepancies/${row.id}/${action}`, action === 'review' ? { note: '' } : {}, { headers: authHeaders });
      toast.success(action === 'review' ? 'Marked reviewed' : 'Reopened');
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setCounts((c) => (action === 'review'
        ? { open: Math.max(0, c.open - 1), reviewed: c.reviewed + 1 }
        : { open: c.open + 1, reviewed: Math.max(0, c.reviewed - 1) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await axios.get(`${API}/admin/system/discrepancies/export`, {
        headers: authHeaders, params: { status: tab, q }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledger_discrepancies_${tab}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const totalShortfall = rows.reduce((s, r) => s + (Number(r.shortfall) || 0), 0);

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-4" data-testid="discrepancies-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2"><ClipboardList className="h-5 w-5" /> Ledger Discrepancies</h1>
            <p className="text-sm text-slate-500">Order lines where the shipped quantity was more than the pallet could account for. Check each against physical stock, then mark it reviewed.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-2 rounded border text-sm flex items-center gap-1" data-testid="disc-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={exportXlsx} className="px-3 py-2 rounded bg-slate-900 text-white text-sm flex items-center gap-1" data-testid="disc-export"><Download className="h-4 w-4" /> Export Excel</button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {['open', 'reviewed'].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`disc-tab-${t}`}
              className={`px-3 py-1.5 rounded-full text-sm border ${tab === t ? 'bg-slate-900 text-white' : 'bg-white'}`}>
              {t === 'open' ? 'Open' : 'Reviewed'} ({counts[t] || 0})
            </button>
          ))}
          <div className="flex items-center gap-1 ml-auto">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Search SKU, product or order no." className="border rounded px-3 py-1.5 text-sm w-64" data-testid="disc-search" />
            <button onClick={load} className="p-2 rounded border"><Search className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="text-xs text-slate-500">{rows.length} rows · total shortfall {totalShortfall.toLocaleString()}</div>

        <div className="overflow-x-auto border rounded bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                {['SKU', 'Product', 'Order No.', 'Pallet', 'Location', 'Qty shipped', 'Pallet qty', 'Shortfall', ''].map((h) => (
                  <th key={h} className="px-3 py-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={9} className="px-3 py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</td></tr>
              )}
              {!busy && rows.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Nothing here.</td></tr>
              )}
              {!busy && rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`disc-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono">{r.sku}</td>
                  <td className="px-3 py-2">{r.product_name}</td>
                  <td className="px-3 py-2">{r.order_number}</td>
                  <td className="px-3 py-2">{r.pallet_id}</td>
                  <td className="px-3 py-2">{r.location_id}</td>
                  <td className="px-3 py-2">{r.expected_qty}</td>
                  <td className="px-3 py-2">{r.available_qty}</td>
                  <td className="px-3 py-2 font-semibold text-rose-700">{r.shortfall}</td>
                  <td className="px-3 py-2 text-right">
                    {tab === 'open' ? (
                      <button disabled={busyId === r.id} onClick={() => act(r, 'review')} data-testid={`disc-review-${r.id}`}
                        className="px-2 py-1 rounded bg-emerald-600 text-white text-xs inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Mark reviewed
                      </button>
                    ) : (
                      <button disabled={busyId === r.id} onClick={() => act(r, 'reopen')}
                        className="px-2 py-1 rounded border text-xs inline-flex items-center gap-1">
                        <RotateCcw className="h-3 w-3" /> Reopen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
